import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTourStore } from '@/stores/tourStore';

/**
 * CloseConfirmModal — confirmation shown when the user tries to exit the
 * spotlight tour (close button or ESC). Lets them bail out for good or resume
 * where they left off.
 *
 * Open/close state is read from `tourStore` (`showCloseConfirmModal`).
 * TourProvider opens it via `openCloseConfirmModal()` and re-focuses the
 * spotlight when it closes with the tour still active.
 */
export default function CloseConfirmModal() {
  const showCloseConfirmModal = useTourStore((s) => s.showCloseConfirmModal);
  const closeCloseConfirmModal = useTourStore((s) => s.closeCloseConfirmModal);
  const endTour = useTourStore((s) => s.endTour);

  const handleExplore = () => {
    // Bail out for good — mark the tutorial complete.
    void endTour(true);
  };

  const handleContinue = () => {
    // Just close the modal; TourProvider re-drives the spotlight at the
    // current step so the tour resumes from where it was left off.
    closeCloseConfirmModal();
  };

  return (
    <Dialog
      open={showCloseConfirmModal}
      onOpenChange={(open) => {
        // Soft dismiss (X / outside click) is treated as "continue tour".
        if (!open) handleContinue();
      }}
    >
      {/* z-index above driver.js's 1e9 popover so this dialog is never behind it. */}
      <DialogContent
        className="max-w-md mx-4 z-[1000000002] bg-white border border-hairline rounded-lg"
        overlayClassName="z-[1000000001]"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-ink text-xl">Calling it?</DialogTitle>
          <DialogDescription className="text-base pt-1 text-steel-dark">
            Wanna figure out Zupa yourself, or want to finish the tour?
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3 pt-4">
          <Button
            onClick={handleExplore}
            size="lg"
            variant="outline"
            className="w-full border-hairline text-steel-dark hover:bg-paper rounded-md"
          >
            I'll explore on my own
          </Button>
          <Button
            onClick={handleContinue}
            size="lg"
            className="w-full bg-cargo hover:bg-cargo/90 text-cargo-ink rounded-md focus-visible:ring-ink"
          >
            Continue tour
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
